import React, {useState} from 'react';
import {Box, Collapse, Typography} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import './BannerAccordion.scss';

interface IBannerAccordionProps {
  variant: 'success' | 'warning' | 'error' | 'info';
  startIcon?: React.ReactNode;
  visibleContent: string;
  expandedContent: React.ReactNode;
  defaultExpanded?: boolean;
  dataTestId?: string; // <-- added root-level testID
}

const BannerAccordion: React.FC<IBannerAccordionProps> = ({
  variant,
  startIcon,
  visibleContent,
  expandedContent,
  defaultExpanded = false,
  dataTestId = 'banner-accordion',
}) => {
  const [open, setOpen] = useState(defaultExpanded);

  const handleToggle = () => setOpen((p) => !p);

  const hasExpandedContent = (() => {
    if (!expandedContent) return false;

    // Not a React element
    if (typeof expandedContent !== 'object' || !('props' in expandedContent)) {
      return true;
    }

    const children = (expandedContent as any).props?.children;

    // No children
    if (!children) return false;

    // [] or empty array
    if (
      Array.isArray(children) &&
      children.every((c) => c === null || c === undefined)
    ) {
      return false;
    }

    return true;
  })();

  return (
    <Box className="banner-accordion-parent">
      <Box
        className={`banner-accordion banner-accordion--${variant}`}
        data-testid={dataTestId}
        role="region" // WCAG landmark
        aria-label={`${variant} banner`}
      >
        <Box
          className="banner-accordion__top"
          data-testid={`${dataTestId}-header`}
        >
          {startIcon && (
            <Box
              className="banner-accordion__icon"
              data-testid={`${dataTestId}-start-icon`}
            >
              {startIcon}
            </Box>
          )}
          <Typography
            className="typo-text body3_size banner-accordion__text"
            data-testid={`${dataTestId}-text`}
          >
            {visibleContent}
          </Typography>
          {hasExpandedContent && (
            <Box
              className="banner-accordion__accordion-icon"
              data-testid={`${dataTestId}-toggle`}
              role="button"
              aria-expanded={open}
              aria-controls={`${dataTestId}-content`}
              tabIndex={0} // keyboard accessible
              onClick={handleToggle}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleToggle();
              }}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </Box>
          )}
        </Box>

        {hasExpandedContent && (
          <Collapse
            in={open}
            data-testid={`${dataTestId}-collapse`}
            timeout="auto"
            unmountOnExit
          >
            <Box
              className="banner-accordion__expanded"
              data-testid={`${dataTestId}-content`}
              role="region"
              aria-live="polite" // WCAG: announce expansion
            >
              {expandedContent}
            </Box>
          </Collapse>
        )}
      </Box>
    </Box>
  );
};

export default BannerAccordion;
